/**
 * Read-only file viewer for the Skills detail browser.
 *
 * - At HEAD: renders the file content as plain text inside a scrollable pre.
 * - At a non-HEAD commit: renders the unified diff (when supplied), falling
 *   back to the raw content if no diff is available.
 * - Binary files (encoding === 'base64'): shows a placeholder; we don't render
 *   the bytes.
 */
import type { SkillFileContent } from '../../lib/api-client';

interface Props {
  content: SkillFileContent | null;
  status: 'idle' | 'loading' | 'success' | 'error';
  error?: string | undefined;
  /** When true, prefer the diff view if a diff is present. */
  showAsDiff: boolean;
}

export function SkillFileViewer({ content, status, error, showAsDiff }: Props) {
  if (status === 'loading') {
    return (
      <div style={{ padding: 16, color: 'var(--text-mute)', fontSize: 12 }}>Loading…</div>
    );
  }
  if (status === 'error') {
    return (
      <div style={{ padding: 16, color: 'var(--kind-failure)', fontSize: 12 }}>
        {error ?? 'Failed to load file.'}
      </div>
    );
  }
  if (!content) {
    return (
      <div style={{ padding: 16, color: 'var(--text-mute)', fontSize: 12 }}>
        Select a file to view its contents.
      </div>
    );
  }

  if (content.encoding === 'base64') {
    return (
      <div style={{ padding: 16, color: 'var(--text-mute)', fontSize: 12 }}>
        Binary file ({content.mime}) — preview not available.
      </div>
    );
  }

  const diff = content.diff_against_parent;
  if (showAsDiff && diff && diff.trim()) {
    return <DiffBlock diff={diff} />;
  }

  return (
    <pre
      style={{
        margin: 0,
        padding: 16,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        fontSize: 12,
        lineHeight: 1.5,
        color: 'var(--text)',
        background: 'var(--bg-alt)',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
      }}
    >
      {content.content}
    </pre>
  );
}

/**
 * Minimal unified-diff renderer. We don't want a heavy library here — emotion-
 * based diff viewers break the Shadow DOM boundary, and the parsing rules for
 * a single-file unified diff are simple enough to inline.
 */
function DiffBlock({ diff }: { diff: string }) {
  const lines = diff.split('\n');
  return (
    <div
      style={{
        margin: 0,
        padding: 0,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        fontSize: 12,
        lineHeight: 1.5,
        background: 'var(--bg-alt)',
        overflowX: 'auto',
      }}
    >
      {lines.map((line, i) => {
        let bg = 'transparent';
        let color = 'var(--text)';
        if (line.startsWith('+++') || line.startsWith('---') || line.startsWith('diff ') || line.startsWith('index ')) {
          color = 'var(--text-mute)';
        } else if (line.startsWith('@@')) {
          bg = 'var(--bg-alt2, transparent)';
          color = 'var(--text-soft)';
        } else if (line.startsWith('+')) {
          bg = 'rgba(46, 160, 67, 0.15)';
          color = 'var(--kind-success, #2da44e)';
        } else if (line.startsWith('-')) {
          bg = 'rgba(207, 34, 46, 0.15)';
          color = 'var(--kind-failure, #cf222e)';
        }
        return (
          <div
            key={i}
            style={{
              padding: '0 12px',
              background: bg,
              color,
              whiteSpace: 'pre',
            }}
          >
            {line || ' '}
          </div>
        );
      })}
    </div>
  );
}
